import { Button, IconClose, useSmallToMediaQuery } from '@madpaws/design-system';
import Cookies from 'js-cookie';
import getConfig from 'next/config';
import React, { useEffect, useState } from 'react';

import { ImageWithFallback } from '~/common/components/ImageWithFallback/ImageWithFallback';
import {
  CREATE_MOBILE_APP_BANNER_EPPO_ID,
  CREATE_MOBILE_APP_BANNER_EPPO_ID_VALUES,
} from '~/components/Eppo/constants';
import { useEppoStringAssignment } from '~/components/Eppo/helpers';

import styles from './MobileAppDownloadBanner.module.css';

import type { ReactElement } from 'react';

const MOBILE_BANNER_CLOSED_COOKIE_NAME = 'mobile_banner_closed';

const SCHEME = 'madpaws://';
const IOS_APP_STORE_URL = 'https://apps.apple.com/app/id983899686';
const ANDROID_PACKAGE = 'au.com.madpaws';
const ANDROID_PLAY_STORE_URL = `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE}`;
const PATH = 'home?ref=app_banner';

const { publicRuntimeConfig } = getConfig();

export const MobileAppDownloadBanner = (): ReactElement => {
  // const [show, setShow] = useState(false);
  const [device, setDevice] = useState<'ios' | 'android' | 'other'>('other');

  const isMediumToViewport = useSmallToMediaQuery();

  const [isBannerClosed, setIsBannerClosed] = React.useState(
    !!Cookies.get(MOBILE_BANNER_CLOSED_COOKIE_NAME)
  );

  const isMobileAppDownloadBannerEnabled =
    useEppoStringAssignment(CREATE_MOBILE_APP_BANNER_EPPO_ID) ===
    CREATE_MOBILE_APP_BANNER_EPPO_ID_VALUES.ON;

  const detectDevice = (): 'ios' | 'android' | 'other' => {
    const ua = navigator.userAgent.toLowerCase();
    if (/android/.test(ua)) {
      return 'android';
    }
    const iOS = /iphone|ipod|ipad/.test(ua) || (/macintosh/.test(ua) && 'ontouchend' in document);
    if (iOS) {
      return 'ios';
    }
    return 'other';
  };

  useEffect(() => {
    const d = detectDevice();
    setDevice(d);
  }, []);

  if (!isMediumToViewport || !isMobileAppDownloadBannerEnabled || isBannerClosed) {
    return <></>;
  }

  const handleClose = (): void => {
    Cookies.set(MOBILE_BANNER_CLOSED_COOKIE_NAME, 'true');
    setIsBannerClosed(true);
  };

  const openApp = (): void => {
    const schemeUrl = PATH ? `${SCHEME}${PATH}` : SCHEME;

    if (device === 'android') {
      const intentUrl =
        `intent://${PATH}#Intent;` +
        `scheme=${SCHEME.replace('://', '')};` +
        `package=${ANDROID_PACKAGE};` +
        `S.browser_fallback_url=${encodeURIComponent(ANDROID_PLAY_STORE_URL)};` +
        `end`;

      try {
        window.location.href = intentUrl;
      } catch {
        const t = setTimeout(() => {
          window.location.href = ANDROID_PLAY_STORE_URL;
        }, 1200);
        document.addEventListener(
          'visibilitychange',
          () => {
            if (document.visibilityState === 'hidden') {
              clearTimeout(t);
            }
          },
          { once: true }
        );
        window.location.href = schemeUrl;
      }
      return;
    }

    if (device === 'ios') {
      const t = setTimeout(() => {
        window.location.href = IOS_APP_STORE_URL;
      }, 1400);
      document.addEventListener(
        'visibilitychange',
        () => {
          if (document.visibilityState === 'hidden') {
            clearTimeout(t);
          }
        },
        { once: true }
      );
      window.location.href = schemeUrl;
      return;
    }

    window.open(IOS_APP_STORE_URL, '_blank');
  };

  return (
    <div className={styles.root}>
      <div className={styles.leftSection}>
        <div onClick={handleClose}>
          <IconClose size="small2x" />
        </div>
        <ImageWithFallback
          alt="Picture of a pet"
          fallback={`${publicRuntimeConfig.staticPath}/images/MobileBanner/logo.png`}
          loading="lazy"
          webpSrc={`${publicRuntimeConfig.staticPath}/images/MobileBanner/logo.png`}
          width="60px"
        />
        <div className={styles.textSection}>
          <p className={styles.title}>Mad Paws App</p>
          <ImageWithFallback
            alt="Picture of a pet"
            fallback={`${publicRuntimeConfig.staticPath}/images/MobileBanner/stars.png`}
            loading="lazy"
            webpSrc={`${publicRuntimeConfig.staticPath}/images/MobileBanner/stars.png`}
            width="60px"
          />
          <p>4.4K Reviews</p>
        </div>
      </div>
      <Button label="Open" onClick={openApp} variant="primary" />
    </div>
  );
};
